import { CONFIRM_SPACE_AUTH_DIALOG, ConfirmAdditionalSpaceAuthorizationComponent } from './confirm-additional-space-authorization/confirm-additional-space-authorization.component';
import { SpaceAccessBlockedDialogComponent } from './space-access-blocked-dialog/space-access-blocked-dialog.component';
import { CoreStore } from '../../../../core/store/core.store';
import { LoginService } from '../../../login/services/login.service';
import { getLoginJWTToken, isTopBarVisible } from '../../../login/store/login.selectors';
import { SpaceAccessForbiddenDialogComponent } from '../space-access-forbidden-dialog/space-access-forbidden-dialog.component';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { canAccessSpace, getAvailableSpaces } from '@ppl/auth';
import { getAppAbsoluteURL } from '@ppl/core';
import { SpaceAccessStatus } from '@ppl/graphql-user-api';
import { I18nService } from '@ppl/i18n';
import { PplDialogService } from '@ppl/ui/dialog';
import { BehaviorSubject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import type {
  OnDestroy,
  OnInit
} from '@angular/core';
import type { Space } from '@ppl/graphql-user-api';
import type { Observable } from 'rxjs';
import type { Translations } from '../../../../../translations';
import type { ConfirmAdditionalSpaceAuthorizationData } from './confirm-additional-space-authorization/confirm-additional-space-authorization.component';
import type { SpaceAccessBlockedData } from './space-access-blocked-dialog/space-access-blocked-dialog.component';


@Component({
  selector: 'ppl-select-space',
  templateUrl: './select-space.component.html',
  styleUrls: ['./select-space.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SelectSpaceComponent implements OnInit, OnDestroy {
  showLoader$ = new BehaviorSubject(false);

  isTopBarVisible = this.store.get(isTopBarVisible);
  spaces$: Observable<Space[]> = this.store.select(getAvailableSpaces);

  private pageShowHandler: (event: PageTransitionEvent) => void;

  constructor(
    private store: CoreStore,
    private dialogService: PplDialogService,
    private loginService: LoginService,
    private i18nService: I18nService<typeof Translations>
  ) { }

  ngOnInit() {
    this.pageShowHandler = () => {
      this.showLoader$.next(false);
    };
    window.addEventListener('pageshow', this.pageShowHandler);
  }

  ngOnDestroy() {
    if (this.pageShowHandler) {
      window.removeEventListener('pageshow', this.pageShowHandler);
      this.pageShowHandler = null;
    }
  }

  canAccessSpace(space: Space) {
    return canAccessSpace(space);
  }

  isLoginRequired(space: Space) {
    return space.accessStatus === SpaceAccessStatus.IdpLoginRequired;
  }

  isTwoStepVerificationNeeded(space: Space) {
    return space.accessStatus === SpaceAccessStatus.TwoFARequired;
  }

  isAccessFromRestrictedIP(space: Space) {
    return space.accessStatus === SpaceAccessStatus.AccessFromRestrictedIP;
  }

  isSessionReloginRequired(space: Space) {
    return space.accessStatus === SpaceAccessStatus.SessionReloginRequired;
  }

  onSpaceSelect(space: Space) {
    if (!canAccessSpace(space)) {
      const dialogRef = this.dialogService.open(SpaceAccessForbiddenDialogComponent, {
        autoFocus: false
      });
      dialogRef.componentInstance.space = space;
      return false;
    }

    const redirect = (changeUsersDefaultIdp = false) => {
      this.showLoader$.next(true);
      this.loginService.getRedirectUrlAfterSpaceSelect(space, changeUsersDefaultIdp).subscribe((response) => {
        window.location.href = getAppAbsoluteURL(response.redirect_url);
      }, (error) => {
        const errorMessage: string = error instanceof HttpErrorResponse
          ? error.statusText
          : error.toString();
        this.dialogService.alert({
          text: this.i18nService.translate(`Something_went_wrong.`),
          description: this.i18nService.translate('Error:_%errorMessage%', { errorMessage })
        });
        this.showLoader$.next(false);
      });
    };

    if (this.isLoginRequired(space)) {
      setTimeout(() => {
        this.dialogService.open<
          ConfirmAdditionalSpaceAuthorizationComponent,
          ConfirmAdditionalSpaceAuthorizationData
        >(ConfirmAdditionalSpaceAuthorizationComponent, {
          autoFocus: false,
          data: {
            requiredIdp: space.idp
          }
        }).afterClosed()
          .pipe(filter(result => result))
          .subscribe((result) => {
            redirect(result === CONFIRM_SPACE_AUTH_DIALOG.WITH_DEFAULT_IDP_CHANGE);
          });
      });
      return false;
    }

    if (this.isTwoStepVerificationNeeded(space) || this.isSessionReloginRequired(space)) {
      redirect();
      return false;
    }

    if (this.isAccessFromRestrictedIP(space)) {
      this.dialogService.open<
        SpaceAccessBlockedDialogComponent,
        SpaceAccessBlockedData
      >(SpaceAccessBlockedDialogComponent, {
        data: {
          message: this.i18nService.translate(`In_accordance_with_your_company's_security_policy,_you_cannot_access_this_space_from_your_current_location._Please_select_another_space.`)
        }
      });
      return false;
    }

    if (this.store.get(getLoginJWTToken)) {
      redirect();
      return false;
    }

    this.showLoader$.next(true);
    window.location.href = `${space.appBaseUrl}/space/${space.id}`;

    return false;
  }
}
